import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  listConversations,
  createConversation,
  getConversationMessages,
  streamMessage,
  uploadImage,
  updateConversationTitleApi,
  updateConversationTags
} from '../services/api';
import { ConversationSummary, Message, ChatHistory, ConversationCreateResponse } from '../types';
import { useAuth } from './AuthContext'; // Assuming AuthContext provides user info

// Helper function to extract first 10 words from text
const extractFirstTenWords = (text: string): string => {
  const words = text.trim().split(/\s+/);
  const firstTenWords = words.slice(0, 10).join(' ');
  return firstTenWords || 'New Chat'; // Fallback if no words
};

interface ChatContextState {
  conversations: ConversationSummary[];
  currentConversationId: number | null;
  currentVisitNumber: number | null;
  currentPromptVersionId: number | undefined;
  messages: Message[];
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  isBrainProcessing: boolean;
  error: string | null;
  fetchConversations: (tags?: string[], tagMode?: 'any' | 'all') => Promise<void>;
  selectConversation: (conversationId: number | null) => void;
  handleSendMessage: (content: string, purpose: string) => Promise<void>;
  handleSendMessageWithAutoConversation: (content: string, purpose: string, attachment?: Blob | File | null, imageMode?: string | null) => Promise<void>;
  handleCreateConversation: (title?: string, options?: { silent?: boolean }) => Promise<number | null>;

  // New state for Brain Config View
  isConfigViewActive: boolean;
  setIsConfigViewActive: (isActive: boolean) => void;

  // New state and function for updating Conversation Title
  isUpdatingConversationTitle: boolean;
  updateConversationTitleError: string | null;
  handleUpdateConversationTitle: (conversationId: number, newTitle: string) => Promise<void>;
  handleUpdateConversationTags: (conversationId: number, tags: string[]) => Promise<void>;

  // Onboarding-related state
  preparationStatus: string | null;
  isPreparingConversation: boolean;
  isInitializingForNewUser: boolean; // New state for initial onboarding setup
}

const ChatContext = createContext<ChatContextState | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [currentVisitNumber, setCurrentVisitNumber] = useState<number | null>(null);
  const [currentPromptVersionId, setCurrentPromptVersionId] = useState<number | undefined>(undefined);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isBrainProcessing, setIsBrainProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Onboarding-related state
  const [preparationStatus, setPreparationStatus] = useState<string | null>(null);
  const [isPreparingConversation, setIsPreparingConversation] = useState(false);
  const [isInitializingForNewUser, setIsInitializingForNewUser] = useState(false);

  // New state for Brain Config
  const [isConfigViewActive, setIsConfigViewActive] = useState(false);

  // New state for updating conversation title
  const [isUpdatingConversationTitle, setIsUpdatingConversationTitle] = useState(false);
  const [updateConversationTitleError, setUpdateConversationTitleError] = useState<string | null>(null);

  const { user } = useAuth(); // Get user from AuthContext to fetch data only when logged in
  const location = useLocation(); // Get current route
  const experienceMode = React.useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('mode') === 'try' ? 'try' : undefined;
  }, [location.search]);
  const cleanupPollingRef = useRef<(() => void) | null>(null); // Ref to hold the cleanup function
  const hasAutoCreatedConversationRef = useRef<boolean>(false); // Track if we've auto-created a conversation in this session

  // --- Function to update title based on first message ---
  const updateTitleFromFirstMessage = useCallback((conversationId: number, content: string, currentMessagesLength: number) => {
    if (currentMessagesLength === 0) { // This is the first message in the conversation
      const newTitle = extractFirstTenWords(content);
      return newTitle;
    }
    return null;
  }, []);

  // --- Fetch Conversations --- 
  const fetchConversations = useCallback(async (tags?: string[], tagMode?: 'any' | 'all') => {
    if (!user) return; // Don't fetch if not logged in
    setIsLoadingConversations(true);
    setError(null);
    try {
      const fetchedConversations = await listConversations(tags, tagMode);
      console.log(`[OnboardingDebug] Fetched ${fetchedConversations.length} existing conversations`);
      setConversations(fetchedConversations);
      
      // Auto-create new conversation on every login (implements visit-based onboarding)
      // Each login session = new visit = new conversation with appropriate prompt
      // Use ref to ensure we only create once per session
      // ONLY run on /chat route - don't auto-create when user is on other pages like /prompts
      
      // A student who still owes the entry assessment is about to be sent to
      // it, so don't open a visit/conversation for them yet. Once it's done the
      // refreshed user clears the flag and this runs again.
      const isOnChatRoute =
        !user.entry_assessment_pending &&
        (location.pathname === '/chat' ||
          location.pathname === '/' ||
          location.pathname === '/try');

      // Restore the chat the student was viewing before a page reload.
      // currentConversationId is in-memory only, so on reload we'd otherwise
      // auto-create a brand-new "New Adventure" and lose their place. The active
      // id is stashed in sessionStorage (survives reload, clears on a fresh
      // tab/login) — so a reload lands back in the same conversation while a
      // genuine new login still gets the visit-based fresh conversation below.
      if (!hasAutoCreatedConversationRef.current && isOnChatRoute && user) {
        const savedIdRaw = sessionStorage.getItem(`activeConversationId_${user.id}`);
        const savedId = savedIdRaw ? parseInt(savedIdRaw, 10) : NaN;
        const savedConv = Number.isFinite(savedId)
          ? fetchedConversations.find((c) => c.id === savedId)
          : undefined;
        if (savedConv) {
          hasAutoCreatedConversationRef.current = true; // skip the auto-create below
          setCurrentConversationId(savedConv.id);
          setCurrentVisitNumber((savedConv as any).visit_number ?? null);
          setCurrentPromptVersionId((savedConv as any).prompt_version_id);
          try {
            const history: ChatHistory = await getConversationMessages(savedConv.id);
            setMessages(history.messages || []);
          } catch (restoreErr) {
            console.error('[OnboardingDebug] Failed to restore saved conversation:', restoreErr);
            setMessages([]);
          }
          return; // outer finally clears isLoadingConversations
        }
      }
      // Eager opening on login (once per session): the coach greets the moment
      // the student lands — its message leads with the class's most recent
      // context (completed topics / homework / exams / pushed session) and the
      // syllabus card renders above it (see MessageList). Same experience as
      // clicking "New Adventure". Runs sequentially AFTER the list fetch so the
      // new conversation isn't clobbered by setConversations above.
      if (!hasAutoCreatedConversationRef.current && isOnChatRoute) {
        console.log(`[OnboardingDebug] User login detected on chat route - automatically creating new conversation (Visit ${fetchedConversations.length + 1})`);
        hasAutoCreatedConversationRef.current = true; // Mark as created
        
        try {
          // Show loading screen (especially important for first-time users)
          const isNewUser = fetchedConversations.length === 0;
          if (isNewUser) {
            setIsInitializingForNewUser(true);
          }
          setIsPreparingConversation(true);
          
          const response: ConversationCreateResponse = await createConversation("New Chat");
          const newConversation = response.conversation;
          
          console.log(`[OnboardingDebug] Conversation created with visit_number=${response.visit_number}, prompt_version_id=${newConversation.prompt_version_id}`);
          
          // Set preparation status for loading UI
          setPreparationStatus(response.preparation_status);
          
          // Set visit number and prompt version
          setCurrentVisitNumber(response.visit_number);
          setCurrentPromptVersionId(newConversation.prompt_version_id);
          
          console.log(`[OnboardingDebug] Set currentVisitNumber to ${response.visit_number}`);
          
          // Add conversation to list
          setConversations([newConversation, ...fetchedConversations]);
          setCurrentConversationId(newConversation.id);
          
          // Fetch actual messages from database to get real message IDs (including opening message)
          // This is important for pipeline steps functionality
          if (response.ai_opening_message) {
            try {
              const history: ChatHistory = await getConversationMessages(newConversation.id);
              setMessages(history.messages || []);
            } catch (msgErr) {
              console.error('[OnboardingDebug] Failed to fetch opening message:', msgErr);
              // Fallback: show the message with a note that pipeline steps won't work
              const aiMessage: Message = {
                id: Date.now(), // Temporary ID - pipeline steps won't work
                content: response.ai_opening_message || '',
                is_user: false,
                timestamp: new Date().toISOString(),
                status: 'sent'
              };
              setMessages([aiMessage]);
            }
          }
          
          console.log(`[OnboardingDebug] Conversation created successfully for Visit ${response.visit_number}:`, newConversation.id);
        } catch (createErr: any) {
          console.error('[OnboardingDebug] Failed to auto-create conversation:', createErr);
          // Handle 503 errors specially (preparation timeout)
          if (createErr.status === 503) {
            setError('Unable to prepare your conversation at this time. Please try again in a moment.');
          } else {
            setError(createErr.message || 'Failed to create conversation');
          }
        } finally {
          setIsPreparingConversation(false);
          setIsInitializingForNewUser(false); // Clear loading screen
        }
      } else if (hasAutoCreatedConversationRef.current) {
        console.log(`[OnboardingDebug] Skipping auto-creation - already created in this session`);
      } else if (!isOnChatRoute) {
        console.log(`[OnboardingDebug] Skipping auto-creation - not on chat route (current: ${location.pathname})`);
      }
      
      // Optionally select the most recent conversation by default
      // if (fetchedConversations.length > 0 && currentConversationId === null) {
      //   setCurrentConversationId(fetchedConversations[0].id);
      // }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversations');
    } finally {
      setIsLoadingConversations(false);
    } 
  }, [user, location.pathname]); // Depend on user and current route

  // --- Fetch Messages for a Conversation --- 
  const fetchMessages = useCallback(async (conversationId: number) => {
    if (!user) return;
    setIsLoadingMessages(true);
    setIsBrainProcessing(false);
    setError(null);
    setMessages([]); // Clear previous messages
    try {
      const history: ChatHistory = await getConversationMessages(conversationId);
      setMessages(history.messages || []); // Assuming ChatHistory has a messages array
    } catch (err: any) {
      setError(err.message || 'Failed to fetch messages');
      setMessages([]); // Clear messages on error
    } finally {
      setIsLoadingMessages(false);
    }
  }, [user]);

  // --- Select Conversation --- 
  const selectConversation = useCallback((conversationId: number | null) => {
    setCurrentConversationId(conversationId);
    setIsBrainProcessing(false);
    setPreparationStatus(null); // Clear preparation status
    if (conversationId !== null) {
      // Find the conversation and set visit number + prompt version
      const conversation = conversations.find(c => c.id === conversationId);
      setCurrentVisitNumber(conversation?.visit_number ?? null);
      setCurrentPromptVersionId((conversation as any)?.prompt_version_id);
      fetchMessages(conversationId);
      setIsConfigViewActive(false); // Ensure config view is not active when a chat is selected
    } else {
      setMessages([]); // Clear messages if no conversation is selected
      setCurrentVisitNumber(null);
      setCurrentPromptVersionId(undefined);
    }
  }, [fetchMessages, conversations]);

  // --- Update Conversation Title ---
  const handleUpdateConversationTitle = useCallback(async (conversationId: number, newTitle: string) => {
    if (!user) {
      setUpdateConversationTitleError("User not authenticated.");
      return;
    }
    if (!newTitle.trim()) {
      setUpdateConversationTitleError("Title cannot be empty.");
      // Or, revert to original, or let UI handle this (e.g. disable save if empty)
      return;
    }

    setIsUpdatingConversationTitle(true);
    setUpdateConversationTitleError(null);

    try {
      const updatedConversation = await updateConversationTitleApi(conversationId, newTitle.trim());

      setConversations(prevConvs =>
        prevConvs.map(conv =>
          conv.id === conversationId ? { 
            ...conv, // Spread existing summary fields
            title: updatedConversation.title, 
            updated_at: updatedConversation.updated_at 
          } : conv
        )
      );

    } catch (err: any) {
      console.error("Failed to update conversation title:", err);
      setUpdateConversationTitleError(err.message || 'An unknown error occurred while updating title');
    } finally {
      setIsUpdatingConversationTitle(false);
    }
  }, [user]);

  const handleUpdateConversationTags = useCallback(async (conversationId: number, tags: string[]) => {
    const updatedConversation = await updateConversationTags(conversationId, tags);
    setConversations((prevConvs) =>
      prevConvs.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              tags: updatedConversation.tags,
            }
          : conv
      )
    );
  }, []);

  // --- Create Conversation --- 
  const handleCreateConversation = useCallback(async (title?: string, options?: { silent?: boolean }): Promise<number | null> => {
    if (!user) return null;
    const silent = options?.silent ?? false;
    setError(null);
    // In silent mode (lazy-create when the student sends the first message /
    // picks a topic) we DON'T take over the screen with the "preparing" splash —
    // the send flow already shows the message optimistically and streams the reply.
    if (!silent) setIsPreparingConversation(true);
    try {
      const response: ConversationCreateResponse = await createConversation(title);
      const newConversation = response.conversation;
      
      // Set preparation status for loading UI
      setPreparationStatus(response.preparation_status);
      
      // Set visit number and prompt version
      setCurrentVisitNumber(response.visit_number);
      setCurrentPromptVersionId(newConversation.prompt_version_id);
      
      // Add conversation to list
      setConversations(prev => [newConversation, ...prev]); // Add to top of list
      setCurrentConversationId(newConversation.id);
      
      // Fetch actual messages from database to get real message IDs (including opening message)
      // This is important for pipeline steps functionality
      if (response.ai_opening_message) {
        // In silent mode the caller has already added optimistic bubbles, so
        // PREPEND the coach's opening message above them instead of replacing.
        const merge = (opening: Message[]) =>
          silent ? setMessages(prev => [...opening, ...prev]) : setMessages(opening);
        try {
          const history: ChatHistory = await getConversationMessages(newConversation.id);
          merge(history.messages || []);
        } catch (msgErr) {
          console.error('Failed to fetch opening message:', msgErr);
          // Fallback: show the message with temporary ID (pipeline steps won't work)
          const aiMessage: Message = {
            id: Date.now(), // Temporary ID - pipeline steps won't work
            content: response.ai_opening_message,
            is_user: false,
            timestamp: new Date().toISOString(),
            status: 'sent'
          };
          merge([aiMessage]);
        }
      } else if (!silent) {
        setMessages([]); // Clear messages for new chat (never wipe optimistic bubbles)
      }
      
      setIsConfigViewActive(false); // Ensure config view is not active
      return newConversation.id;
    } catch (err: any) {
      // Handle 503 errors specially (preparation timeout)
      if (err.status === 503) {
        setError('Unable to prepare your conversation at this time. Please try again in a moment.');
      } else {
        setError(err.message || 'Failed to create conversation');
      }
      return null;
    } finally {
      if (!silent) setIsPreparingConversation(false);
    }
  }, [user]);


  // --- Send Message --- 
  const handleSendMessage = useCallback(async (content: string, purpose: string = "chat") => {
    if (currentConversationId === null) {
      setError('No conversation selected');
      return;
    }

    // Clear any previous polling before starting a new send
    if (cleanupPollingRef.current) {
        cleanupPollingRef.current();
        cleanupPollingRef.current = null;
    }
    
    setIsSendingMessage(true);
    setIsBrainProcessing(false);
    setError(null);
    
    const tempUserMessage: Message = {
        id: Date.now(),
        content,
        is_user: true,
        timestamp: new Date().toISOString(),
        status: 'sending',
    };

    // Store current message length before adding new message
    const currentMessagesLength = messages.length;

    setMessages(prev => [...prev, tempUserMessage]);

    // The /stream endpoint saves the user message server-side, so mark the
    // optimistic user bubble as sent and add an AI bubble that fills as tokens
    // stream in (v2 SSE hot path — replaces sendMessage + pollAiResponse).
    const aiTempId = Date.now() + 1;
    let aiContent = '';
    setMessages(prev => prev
      .map(msg => (msg.id === tempUserMessage.id ? { ...msg, status: 'sent' as const } : msg))
      .concat([{ id: aiTempId, content: '', is_user: false, timestamp: new Date().toISOString(), status: 'sending' as const }])
    );

    // Title update from the first user message.
    const newTitle = updateTitleFromFirstMessage(currentConversationId, content, currentMessagesLength);
    if (newTitle) {
      handleUpdateConversationTitle(currentConversationId, newTitle).catch(err => {
        console.error("Failed to update conversation title:", err);
      });
    }

    try {
      setIsBrainProcessing(true);
      await streamMessage(currentConversationId, content, {
        onToken: (text) => {
          aiContent += text;
          setIsBrainProcessing(false);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId ? { ...msg, content: aiContent } : msg
          ));
        },
        onDone: (data) => {
          setIsBrainProcessing(false);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId
              ? {
                  ...msg,
                  id: data.message_id > 0 ? data.message_id : aiTempId,
                  content: aiContent,
                  status: 'sent' as const,
                  curiosity_score: data.curiosity_score ?? undefined,
                }
              : msg
          ));
        },
        onError: (message) => {
          setIsBrainProcessing(false);
          setError(message);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId ? { ...msg, status: 'error' as const } : msg
          ));
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setMessages(prev => prev.map(msg =>
        msg.id === tempUserMessage.id ? { ...msg, status: 'error' as const } : msg
      ));
      setIsBrainProcessing(false);
    } finally {
      setIsSendingMessage(false);
    }
  }, [currentConversationId, experienceMode, messages, updateTitleFromFirstMessage, handleUpdateConversationTitle]);

  // --- Send Message with Auto-Conversation Creation --- 
  const handleSendMessageWithAutoConversation = useCallback(async (content: string, purpose: string = "chat", attachment?: Blob | File | null, imageMode?: string | null) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    // Clear any previous polling before starting a new send
    if (cleanupPollingRef.current) {
        cleanupPollingRef.current();
        cleanupPollingRef.current = null;
    }

    // Store current message length before adding new message
    const currentMessagesLength = messages.length;

    setIsSendingMessage(true);
    setError(null);

    // Show the user's message (and a "thinking" bubble) IMMEDIATELY, before we
    // lazily create the conversation. Picking a topic / sending the first
    // message must never blank the chat behind a full-screen "preparing"
    // splash — the student stays in the chat and watches their reply stream in.
    const tempUserMessage: Message = {
        id: Date.now(),
        content, // image markdown (if any) is appended once the upload finishes
        is_user: true,
        timestamp: new Date().toISOString(),
        status: 'sending',
    };
    const aiTempId = Date.now() + 1;
    let aiContent = '';
    setMessages(prev => [
      ...prev,
      tempUserMessage,
      { id: aiTempId, content: '', is_user: false, timestamp: new Date().toISOString(), status: 'sending' as const },
    ]);
    setIsBrainProcessing(true);

    // Fail the optimistic bubbles and stop the spinners on an early abort.
    const abortSend = (message: string) => {
      setError(message);
      setMessages(prev => prev.map(msg =>
        msg.id === tempUserMessage.id ? { ...msg, status: 'error' as const } : msg
      ));
      setIsSendingMessage(false);
      setIsBrainProcessing(false);
    };

    // Lazily create a conversation on the first send — silently, so no takeover.
    let conversationId = currentConversationId;
    if (conversationId === null) {
      try {
        conversationId = await handleCreateConversation(undefined, { silent: true });
        if (conversationId === null) {
          abortSend('Failed to create conversation');
          return;
        }
      } catch (err: any) {
        abortSend(err.message || 'Failed to create conversation');
        return;
      }
    }

    const convId = conversationId;

    // Upload an attached image (photo / notepad drawing) now that we have a
    // conversation id, then embed it in the already-visible bubble. A failed
    // upload aborts the send cleanly rather than dropping the image silently.
    let attachedImageId: string | null = null;
    if (attachment) {
      try {
        const uploaded = await uploadImage(convId, attachment);
        attachedImageId = uploaded.image_id;
        const imageMarkdown = `\n\n![your work](/api/images/${uploaded.image_id})`;
        setMessages(prev => prev.map(msg =>
          msg.id === tempUserMessage.id ? { ...msg, content: `${content}${imageMarkdown}` } : msg
        ));
      } catch (err: any) {
        abortSend(err.message || 'Image upload failed');
        return;
      }
    }

    // The user's message is now committed to the server-bound send.
    setMessages(prev => prev.map(msg =>
      msg.id === tempUserMessage.id ? { ...msg, status: 'sent' as const } : msg
    ));

    const newTitle = updateTitleFromFirstMessage(convId, content || 'Shared an image', currentMessagesLength);
    if (newTitle) {
      handleUpdateConversationTitle(convId, newTitle).catch(err => {
        console.error("Failed to update conversation title:", err);
      });
    }

    try {
      setIsBrainProcessing(true);
      await streamMessage(convId, content, {
        onToken: (text) => {
          aiContent += text;
          setIsBrainProcessing(false);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId ? { ...msg, content: aiContent } : msg
          ));
        },
        onDone: (data) => {
          setIsBrainProcessing(false);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId
              ? { ...msg, id: data.message_id > 0 ? data.message_id : aiTempId, content: aiContent, status: 'sent' as const, curiosity_score: data.curiosity_score ?? undefined }
              : msg
          ));
        },
        onError: (message) => {
          setIsBrainProcessing(false);
          setError(message);
          setMessages(prev => prev.map(msg =>
            msg.id === aiTempId ? { ...msg, status: 'error' as const } : msg
          ));
        },
      }, attachedImageId, imageMode);
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setMessages(prev => prev.map(msg =>
        msg.id === tempUserMessage.id ? { ...msg, status: 'error' as const } : msg
      ));
      setIsBrainProcessing(false);
    } finally {
      setIsSendingMessage(false);
    }
  }, [user, currentConversationId, experienceMode, handleCreateConversation, messages, updateTitleFromFirstMessage, handleUpdateConversationTitle]);

  // --- Initial Fetch ---
  useEffect(() => {
    if (user) {
      // Reset the auto-creation flag when user logs in
      hasAutoCreatedConversationRef.current = false;
      console.log(`[OnboardingDebug] User logged in, reset auto-creation flag`);
      fetchConversations();
    } else {
       // User logged out - reset everything
       console.log(`[OnboardingDebug] User logged out, resetting state`);
       setConversations([]);
       setCurrentConversationId(null);
       setCurrentVisitNumber(null);
       setCurrentPromptVersionId(undefined);
       setMessages([]);
       setError(null);
       setIsBrainProcessing(false);
       hasAutoCreatedConversationRef.current = false; // Reset flag on logout
       if (cleanupPollingRef.current) cleanupPollingRef.current(); // Use ref for cleanup
    }
    // Cleanup polling on component unmount or when user changes
    return () => {
      if (cleanupPollingRef.current) {
          cleanupPollingRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user - fetchConversations causes double-trigger bug

  // The initial fetch above fires while the route is still /login, where the
  // eager login-opening is (correctly) skipped. Re-run once navigation lands on
  // the chat route so the coach's greeting actually fires. The ref guard makes
  // this a no-op after the opening has been created once this session.
  useEffect(() => {
    const isOnChatRoute =
      location.pathname === '/chat' || location.pathname === '/' || location.pathname === '/try';
    if (user && !user.entry_assessment_pending && isOnChatRoute && !hasAutoCreatedConversationRef.current) {
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  // Remember the active conversation across reloads (restored in
  // fetchConversations). sessionStorage survives a reload but clears on a fresh
  // tab/login, so visit-based onboarding still fires for genuine new sessions.
  useEffect(() => {
    if (user && currentConversationId != null) {
      sessionStorage.setItem(`activeConversationId_${user.id}`, String(currentConversationId));
    }
  }, [user, currentConversationId]);

  // --- Value Provided by Context ---
  const value: ChatContextState = {
    conversations,
    currentConversationId,
    currentVisitNumber,
    currentPromptVersionId,
    messages,
    isLoadingConversations,
    isLoadingMessages,
    isSendingMessage,
    isBrainProcessing,
    error,
    fetchConversations,
    selectConversation,
    handleSendMessage,
    handleSendMessageWithAutoConversation,
    handleCreateConversation,
    // New exports for Brain Config
    isConfigViewActive,
    setIsConfigViewActive,
    // New exports for Conversation Title Update
    isUpdatingConversationTitle,
    updateConversationTitleError,
    handleUpdateConversationTitle,
    handleUpdateConversationTags,
    // Onboarding state
    preparationStatus,
    isPreparingConversation,
    isInitializingForNewUser,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// --- Custom Hook to use ChatContext --- 
export const useChat = (): ChatContextState => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}; 
